<script setup>
import { Play, Bookmark, Lightbulb } from 'lucide-vue-next'

const videos = [
  {
    title: 'Quadratic Equations',
    duration: '14 minutes',
    progress: 60,
    bookmarked: true
  },
  {
    title: 'Trigonometry Basics',
    duration: '18 minutes',
    progress: null,
    bookmarked: false
  },
  {
    title: 'Calculus Introduction',
    duration: '22 minutes',
    progress: null,
    bookmarked: false
  }
]
</script>

<template>
  <section class="py-12 md:py-24 bg-white">
    <div class="max-w-7xl mx-auto px-4 md:px-8">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <!-- Left Content - Video Cards -->
        <div class="lg:col-span-6">
          <div class="relative">
            <div class="bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl p-8 shadow-xl">
              <div
                v-for="(video, index) in videos"
                :key="index"
                :class="['bg-white rounded-2xl p-6', index < videos.length - 1 ? 'mb-4' : '']"
              >
                <div class="flex items-center justify-between" :class="video.progress ? 'mb-4' : ''">
                  <div class="flex items-center space-x-3">
                    <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Play class="text-red-600 w-5 h-5" />
                    </div>
                    <div>
                      <p class="font-bold text-gray-900">{{ video.title }}</p>
                      <p class="text-sm text-gray-600">{{ video.duration }}</p>
                    </div>
                  </div>
                  <Bookmark
                    :class="[
                      'w-5 h-5',
                      video.bookmarked ? 'text-blue-600 fill-blue-600' : 'text-gray-400'
                    ]"
                  />
                </div>
                <div v-if="video.progress" class="w-full h-2 bg-gray-200 rounded-full">
                  <div
                    class="h-2 bg-blue-600 rounded-full"
                    :style="{ width: `${video.progress}%` }"
                  ></div>
                </div>
              </div>
            </div>
            <div class="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-300 rounded-full opacity-50 blur-xl"></div>
          </div>
        </div>

        <!-- Right Content -->
        <div class="lg:col-span-6">
          <h2 class="text-3xl md:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Live teaching, backed by revision support
          </h2>
          <p class="text-base md:text-xl text-gray-600 mb-8 leading-relaxed">
            Tutors add recorded explanations and resources to their library
            over time so students can revisit key topics and revise between
            sessions. Recordings support learning but don't replace
            attending live lessons.
          </p>
          <div class="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6">
            <div class="flex items-start space-x-4">
              <Lightbulb class="text-blue-600 w-7 h-7 mt-1 shrink-0" />
              <div>
                <p class="font-semibold text-gray-900 mb-2">Pro tip</p>
                <p class="text-gray-700">
                  Students who regularly review recorded content alongside weekly sessions see up to 40% faster progress
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
